/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { isArtLocation } from "../../../../../actions/Layers/constants";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { path_manual_check } from "../../../../../actions/Tenants/config/constants";
import { useCreateManualAvocadoMutation, useTriggerCheckBusinessRulesMutation } from "../../../../../hooks/mutations";
import { useRouteCheckQuery, useRouteLayerQuery } from "../../../../../hooks/useCurrentEntities";
import { useLayerChecks } from "../../../../../hooks/useLayerChecks";
import { isFilledArray } from "../../../../../utils";
import { toast } from "../../../../../utils/toast";
import { Loading } from "../../../../Helper/Loading";
import { useQcEntities } from "../../QcEntityContext";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";
import { AdvanceRipeningLayout } from "../Layout";
import BoxedFruitTable, { BoxItem } from "../Summary/Boxes";
import CheckImages from "../Summary/CheckImages";
import { AdvanceRipeningChecks } from "./AdvanceRipeningChecks";
import { AdvanceRipeningForm } from "./AdvanceRipeningForm";
import { AdvanceRipeningNavButtons } from "./AdvanceRipeningNavButtons";

const AdvanceRipening = ({ setFetchLayer }: { setFetchLayer: (value: boolean) => void }) => {
    const { check, layer } = useQcEntities();
    const checkQuery = useRouteCheckQuery();
    const isLoading = checkQuery.isLoading;

    const isArtCheck = isArtLocation(check?.location);
    const { data: checks = [] } = useLayerChecks(
        layer?.tenant_id,
        isArtCheck ? layer?.id : undefined,
        isArtCheck ? { check_location: check?.location } : {},
    );
    const checkIsLoading = isLoading;
    const layerIsLoading = useRouteLayerQuery().isLoading;
    const config = useConfig();
    const checkLocationUnit = config.get_check_unit_of_account(layer, check);
    const location = config.get_check_type_config(layer, check);
    const { navigateToCheckAction } = useCheckFlowNavigate();
    const createManualAvocadoMutation = useCreateManualAvocadoMutation();
    const triggerBusinessRulesMutation = useTriggerCheckBusinessRulesMutation();

    const [defectsErrors, setDefectsErrors] = useState<Record<string, string | boolean>>({});

    const box_size = check.box_size || layer.box_size;

    const params = useParams();
    const tenantId = params.tenant_id as string;
    const param_test_id = useMemo(() => parseFloat(params.check_id as string), [params.check_id]);
    const currentFlow = location?.flow?.find((f) => f.path === params.action);
    const flowHasRequiredImages = !currentFlow?.disable_required_images;

    useEffect(() => {
        setFetchLayer(true);
    }, [param_test_id]);

    useEffect(() => {
        if (isFilledArray(Object.values(defectsErrors))) {
            toast.error(Object.values(defectsErrors).join(", "));
        }
    }, [Object.keys(defectsErrors).join(",")]);

    if (!checks || !check || !layer.type) {
        return <Loading />;
    }

    const addFruit = () => createManualAvocadoMutation.mutate(
        { tenantId, checkId: check.check_id, data: { n: 1, unit_of_account: checkLocationUnit } },
        {
            onSuccess: (response) => {
                navigateToCheckAction(`${path_manual_check}/${response.avocado_id}`);
            },
        }
    );

    const onTriggerBusinessRules = async () => {
        try {
            await Promise.all(checks.map((c) => triggerBusinessRulesMutation.mutateAsync({ check: c })));
            toast.success("Business rules triggered for all checks.");
        } catch (error) {
            toast.error("Failed to trigger business rules for all checks.");
        }
    };

    return (
        <div>
            <AdvanceRipeningLayout buttons={<AdvanceRipeningNavButtons addFruit={addFruit} onTriggerBusinessRules={onTriggerBusinessRules} />} hideButtons={isLoading} ripeningDays={<AdvanceRipeningChecks highlight_current={true} />} >
                <p>Advance Ripening Form</p>
                <AdvanceRipeningForm setDefectsErrors={setDefectsErrors} defectsErrors={defectsErrors} />

                {!checkIsLoading && !layerIsLoading && check?.avocados?.length > 0 && (
                    <div>
                        {!box_size && (
                            <BoxItem avocados={check.avocados} check={check} layer={layer} title={`Check ${check.check_id}`} />
                        )}

                        {box_size > 0 && (
                            <BoxedFruitTable check={check} layer={layer} />
                        )}
                    </div>
                )}

                {check.check_id > 0 && flowHasRequiredImages && (
                    <div>
                        <CheckImages tenant_id={tenantId} check_id={check.check_id} required_images={config.get_check_required_images(layer, check)} />
                    </div>
                )}
            </AdvanceRipeningLayout>
        </div>
    );
};

export default AdvanceRipening;
